/**
 * Target Projection
 *
 * Compact display of current vs target emissions with progress bar.
 */

import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';

interface TargetProjectionProps {
  currentEmissions?: number;
  targetEmissions?: number;
  reductionPercentage: number;
  baselineYear: number;
  targetYear: number;
}

export const TargetProjection = ({
  currentEmissions,
  targetEmissions,
  reductionPercentage,
  baselineYear,
  targetYear,
}: TargetProjectionProps) => {
  const { formatCompact } = useEmissionsFormatter();
  const yearsToTarget = targetYear - baselineYear;
  const annualReduction = yearsToTarget > 0 ? reductionPercentage / yearsToTarget : 0;

  return (
    <div className='border rounded p-3 mt-3'>
      <div className='d-flex align-items-center gap-2 mb-3'>
        <i className='fa fa-chart-line text-ThemeTextLight' />
        <span className='fw-medium'>Target Projection</span>
      </div>

      {/* Emissions Display */}
      <div className='row g-3 mb-3'>
        <div className='col-6'>
          <div className='text-ThemeTextLight' style={{ fontSize: '0.75rem' }}>Current Emissions</div>
          <div className='fw-bold' style={{ fontSize: '1.1rem' }}>
            {currentEmissions ? formatCompact(currentEmissions).display : '—'}
          </div>
        </div>
        <div className='col-6'>
          <div className='text-ThemeTextLight' style={{ fontSize: '0.75rem' }}>
            Target Emissions ({targetYear})
          </div>
          <div className='fw-bold text-success' style={{ fontSize: '1.1rem' }}>
            {targetEmissions !== undefined ? formatCompact(targetEmissions).display : '—'}
          </div>
        </div>
      </div>

      {/* Annual Reduction Progress Bar */}
      <div>
        <div className='d-flex justify-content-between align-items-center mb-1'>
          <span className='text-ThemeTextLight' style={{ fontSize: '0.75rem' }}>Annual reduction required</span>
          <span className='text-ThemeTextLight' style={{ fontSize: '0.75rem' }}>
            {annualReduction.toFixed(1)}% per year
          </span>
        </div>
        <div className='progress' style={{ height: 8 }}>
          <div
            className='progress-bar bg-success'
            role='progressbar'
            style={{ width: `${Math.min(annualReduction * 10, 100)}%` }}
            aria-valuenow={annualReduction}
            aria-valuemin={0}
            aria-valuemax={10}
          />
        </div>
      </div>
    </div>
  );
};
